import { AnimatePresence, motion } from 'framer-motion';
import { InfoIcon, X } from 'lucide-react';

import { useModal } from '../../hooks/modalContext';
import { useEffect, useState } from 'react';

import tpLogo from '../../../assets/pages/rewards/tp-logo.png';
import OffersTab from './components/OffersTab/OffersTab';
import HistoryTab from './components/HistoryTab/HistoryTab';
import RedeemRewards from './components/RedeemRewards/RedeemRewards';
import { getRentCredit } from '../../../auth/_requests';
import { usePoints } from '../../hooks/pointsContext';
import { useAuth } from '../../../auth/Auth';
import { Button, Tooltip, Whisper } from 'rsuite';
import WalletHistory from './components/WalletHistory/WalletHistory';
import { ButtonSpinner } from '../../UI/ButtonSpinner/ButtonSpinner';

const Rewards = () => {
  const { setModal, modalState } = useModal();
  const defaultTab = modalState?.openTab || 'offers';
  const defaultSection = modalState?.openSection || 'points';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [activeSection, setActiveSection] = useState(defaultSection);
  const { points, loading } = usePoints();
  const { auth, currentUser } = useAuth();
  const [availableRentCredit, setAvailableRentCredit] = useState<number>();
  const [redeemedRentCredit, setRedeemedRentCredit] = useState<number>();

  const tabs = [
    { id: 'offers', label: 'Offers' },
    { id: 'redeem', label: 'Redeem' },
    { id: 'history', label: 'Activity' },
  ];

  const sections = [
    { id: 'points', label: 'TP points' },
    { id: 'wallet', label: 'TP wallet' },
  ];

  useEffect(() => {
    const fetchRentCredit = async () => {
      try {
        const response = await getRentCredit(auth?.data.token);
        setAvailableRentCredit(response.totalAvailable);
        setRedeemedRentCredit(response.totalRedeemed);
      } catch (error) {
        console.error('Error fetching rent credit:', error);
      }
    };
    fetchRentCredit();
  }, []);
  return (
    <motion.div
      className='relative bg-white rounded-lg shadow-xl w-full max-w-[433px] min-w-0 h-[828px] bg-[#FFF] rounded-[12px] flex flex-col overflow-y-auto max-h-[90vh] mx-4'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <AnimatePresence mode='wait'>
        <motion.div
          key={activeTab} // ensures re-animation when tab changes
          initial={{ y: -300, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -300, opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          className='absolute -top-20 left-20 -translate-x-1/2 z-1 hidden sm:block'
          style={{ display: window.innerWidth < 500 ? 'none' : undefined }}
        >
          {ellipse}
        </motion.div>
      </AnimatePresence>

      <div className='flex justify-end p-4 z-10'>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={() => setModal(null)}
        />
      </div>

      <div className='flex flex-col items-start px-4 sm:px-10 overflow-hidden h-full min-w-0'>
        <span className='text-main font-normal text-[20px] z-10'>My rewards wallet</span>
        <div className='border-b border-[rgba(138,146,171,0.20)] w-full mt-5'>
          <nav className='-mb-px flex justify-between overflow-x-auto' aria-label='Section'>
            {sections.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`
            whitespace-nowrap py-2 px-1 border-b-2 font-medium text-base  z-20
            ${
              activeSection === tab.id
                ? 'border-[var(--primary-color)] text-primary font-bold'
                : 'border-transparent text-main hover:text-gray-700 hover:border-gray-300'
            }
          `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <AnimatePresence mode='wait'>
          {activeSection === 'points' && (
            <motion.div
              key='offers'
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className='w-full h-full flex flex-col items-center justify-start overflow-hidden'
            >
              <div className='relative w-full flex justify-center items-center mt-10'>
                {/* Shadow Element */}
                <div className='absolute -bottom-0 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#0055FF] z-0 pointer-events-none'></div>

                {/* Card */}
                <div className='relative z-10 w-full rounded-[20px] border border-[#698AF5] bg-[linear-gradient(229deg,_#0055FF_-13.57%,_#003BB5_113.63%)] py-6 px-5 sm:px-10'>
                  <div className='flex flex-col h-full gap-10'>
                    <div className='flex justify-between items-center'>
                      <div className='flex flex-col gap-2'>
                        <span className='text-base text-white/80'>TP Points</span>
                        <span className='text-[26px] font-semibold text-white'>
                          {loading ? (
                            <ButtonSpinner />
                          ) : (
                            Number(points?.data?.points?.totalAvailable ?? 0).toLocaleString('en-US')
                          )}
                        </span>
                      </div>
                      <img src={tpLogo} alt='TenantPay Logo' className='h-10 object-contain' />
                    </div>
                    <div className={`flex items-center justify-end`}>
                      <span className='text-sm font-medium text-white/80'>
                        {currentUser?.user?.firstName} {currentUser?.user?.lastName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className='border-b border-[rgba(138,146,171,0.20)] w-full mt-10'>
                <nav className='-mb-px flex justify-between overflow-x-auto' aria-label='Tabs'>
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-base 
            ${
              activeTab === tab.id
                ? 'border-[var(--primary-color)] text-primary font-bold'
                : 'border-transparent text-main hover:text-gray-700 hover:border-gray-300'
            }
          `}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className='w-full flex-grow overflow-auto scrollbar-hide py-10'>
                <AnimatePresence mode='wait'>
                  {activeTab === 'offers' && (
                    <motion.div
                      key='offers'
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className='w-full'
                    >
                      <OffersTab />
                    </motion.div>
                  )}

                  {activeTab === 'history' && (
                    <motion.div
                      key='history'
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className='w-full'
                    >
                      <HistoryTab />
                    </motion.div>
                  )}

                  {activeTab === 'redeem' && (
                    <motion.div
                      key='redeem'
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className='w-full'
                    >
                      <RedeemRewards />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
          {activeSection === 'wallet' && (
            <motion.div
              key='offers'
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className='w-full h-full flex flex-col items-center justify-start overflow-hidden'
            >
              <div className='relative w-full flex justify-center items-center mt-10'>
                {/* Shadow Element */}
                <div className='absolute -bottom-0 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#0055FF] z-0 pointer-events-none'></div>

                {/* Card */}
                <div className='relative z-10 w-full rounded-[20px] border border-[#698AF5] bg-[linear-gradient(229deg,_#039B73_-13.57%,_#003BB5_113.63%)] py-6 px-5 sm:px-10'>
                  <div className='flex flex-col h-full gap-10'>
                    <div className='flex justify-between items-center'>
                      <div className='flex flex-col gap-2'>
                        <span className='text-base text-white/80'>TP Wallet</span>
                        <span className='text-[26px] font-semibold text-white'>
                          ${Number(availableRentCredit).toLocaleString('en-US')}
                        </span>
                      </div>
                      <img src={tpLogo} alt='TenantPay Logo' className='h-10 object-contain' />
                    </div>
                    <div
                      className={`flex items-center ${
                        redeemedRentCredit !== undefined && redeemedRentCredit > 0
                          ? 'justify-between'
                          : 'justify-end'
                      }`}
                    >
                      {redeemedRentCredit !== undefined && redeemedRentCredit > 0 && (
                        <div className='flex items-center gap-1'>
                          <span className='text-sm text-white/90 font-semibold'>
                            +${redeemedRentCredit} Rent credit
                          </span>
                          <Whisper
                            trigger='hover'
                            placement={'right'}
                            controlId={`control-id-top`}
                            className='mt-2'
                            speaker={
                              <Tooltip>
                                <div className='max-w-[200px]'>
                                  This rent credit was earned by redeeming your TenantPay Points and
                                  has already been applied to a past rent payment.
                                </div>
                              </Tooltip>
                            }
                          >
                            <Button appearance='subtle'>
                              <InfoIcon
                                stroke='rgba(255,255,255,0.9)'
                                width={16}
                                className='mb-1'
                              />
                            </Button>
                          </Whisper>
                        </div>
                      )}

                      <span className='text-sm font-medium text-white/80'>
                        {currentUser?.user?.firstName} {currentUser?.user?.lastName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className=' w-full mt-10'>
                <span className='text-medium text-[18px]'>Wallet activity</span>
              </div>

              <div className='w-full flex-grow overflow-auto scrollbar-hide py-10'>
                <AnimatePresence mode='wait'>
                  <motion.div
                    key='history'
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className='w-full'
                  >
                    <WalletHistory />
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export { Rewards };

const ellipse = (
  <svg
    width='225'
    height='388'
    viewBox='0 0 225 388'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    style={{ transform: 'rotate(90deg) scaleX(-1)' }}
  >
    <circle
      data-figma-bg-blur-radius='50'
      cx='194'
      cy='194'
      r='194'
      fill='url(#paint0_linear_42_5205)'
      fillOpacity='0.8'
    />
    <defs>
      <clipPath id='bgblur_0_42_5205_clip_path' transform='translate(50 50)'>
        <circle cx='194' cy='194' r='194' />
      </clipPath>
      <linearGradient
        id='paint0_linear_42_5205'
        x1='72.75'
        y1='388'
        x2='201.132'
        y2='37.8015'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor='#D5DBEC' />
        <stop offset='1' stopColor='white' />
      </linearGradient>
    </defs>
  </svg>
);
